import jsPDF from 'jspdf';

export interface ReceiptData {
  orderNumber: string;
  createdAt: string;
  customer: {
    name: string;
    phone?: string;
    email?: string;
  };
  items: Array<{
    itemName: string;
    quantity: number;
    rate: number;
  }>;
  total: number;
  paymentMethod?: string;
  orderType?: string;
}

export const generateReceiptPDF = (order: ReceiptData): void => {
  const doc = new jsPDF();

  // Page settings
  const pageWidth = doc.internal.pageSize.width;
  const margin = 20;
  let yPosition = 20;

  // Helper function to add text
  const addText = (text: string, x: number, y: number, options?: any) => {
    doc.text(text, x, y, options);
  };

  // Helper function to add centered text
  const addCenteredText = (text: string, y: number, fontSize: number = 12) => {
    doc.setFontSize(fontSize);
    const textWidth = doc.getTextWidth(text);
    const x = (pageWidth - textWidth) / 2;
    addText(text, x, y);
  };

  // Header
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  addCenteredText('Restaurant Receipt', yPosition);
  yPosition += 10;

  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  addCenteredText('Voice Order System', yPosition);
  yPosition += 6;
  addCenteredText('123 Restaurant Street, City', yPosition);
  yPosition += 6;
  addCenteredText('Phone: (555) 123-VOICE', yPosition);
  yPosition += 15;

  // Draw line
  doc.setDrawColor(200, 200, 200);
  doc.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 10;

  // Order Information - Left Column
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  addText('Receipt Number:', margin, yPosition);
  doc.setFont('helvetica', 'normal');
  addText(order.orderNumber, margin + 40, yPosition);
  yPosition += 8;

  doc.setFont('helvetica', 'bold');
  addText('Customer:', margin, yPosition);
  doc.setFont('helvetica', 'normal');
  addText(order.customer.name, margin + 40, yPosition);
  yPosition += 8;

  doc.setFont('helvetica', 'bold');
  addText('Payment:', margin, yPosition);
  doc.setFont('helvetica', 'normal');
  addText(order.paymentMethod || 'Cash', margin + 40, yPosition);
  yPosition += 8;

  // Order Information - Right Column (Date/Time)
  const rightColX = pageWidth / 2 + 20;
  yPosition -= 24; // Go back up to align with receipt number

  doc.setFont('helvetica', 'bold');
  addText('Date:', rightColX, yPosition);
  doc.setFont('helvetica', 'normal');
  const orderDate = new Date(order.createdAt);
  addText(orderDate.toLocaleDateString(), rightColX + 30, yPosition);
  yPosition += 8;

  doc.setFont('helvetica', 'bold');
  addText('Time:', rightColX, yPosition);
  doc.setFont('helvetica', 'normal');
  addText(orderDate.toLocaleTimeString(), rightColX + 30, yPosition);
  yPosition += 8;

  doc.setFont('helvetica', 'bold');
  addText('Type:', rightColX, yPosition);
  doc.setFont('helvetica', 'normal');
  addText(order.orderType || 'Dine-in', rightColX + 30, yPosition);
  yPosition += 16;

  // Draw line
  doc.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 10;

  // Items header
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  addText('Item', margin, yPosition);
  addText('Qty', pageWidth - 120, yPosition);
  addText('Price', pageWidth - 80, yPosition);
  addText('Total', pageWidth - margin, yPosition, { align: 'right' });
  yPosition += 2;

  doc.setDrawColor(150, 150, 150);
  doc.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 8;

  // Items
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');

  order.items.forEach((item) => {
    const itemTotal = item.quantity * item.rate;

    // Handle long item names
    const itemName = item.itemName.length > 35
      ? item.itemName.substring(0, 32) + '...'
      : item.itemName;

    addText(itemName, margin, yPosition);
    addText(item.quantity.toString(), pageWidth - 120, yPosition);
    addText(`£${item.rate.toFixed(2)}`, pageWidth - 80, yPosition);
    addText(`£${itemTotal.toFixed(2)}`, pageWidth - margin, yPosition, { align: 'right' });
    yPosition += 7;

    // Check if we need a new page
    if (yPosition > 270) {
      doc.addPage();
      yPosition = 20;
    }
  });

  yPosition += 5;

  // Draw line
  doc.setDrawColor(200, 200, 200);
  doc.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 10;

  // Totals
  doc.setFont('helvetica', 'normal');
  addText('Subtotal:', pageWidth - margin - 60, yPosition);
  addText(`£${order.total.toFixed(2)}`, pageWidth - margin, yPosition, { align: 'right' });
  yPosition += 8;

  // Grand Total
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setDrawColor(50, 50, 50);
  doc.line(pageWidth - margin - 80, yPosition - 2, pageWidth - margin, yPosition - 2);
  yPosition += 5;
  addText('Total:', pageWidth - margin - 60, yPosition);
  addText(`£${order.total.toFixed(2)}`, pageWidth - margin, yPosition, { align: 'right' });
  yPosition += 15;

  // Footer
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  addCenteredText('✓ Payment Received', yPosition);
  yPosition += 8;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  addCenteredText('Thank you for your order!', yPosition);
  yPosition += 6;

  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  addCenteredText('This is an official receipt for your order', yPosition);
  yPosition += 5;
  addCenteredText('For support: support@restaurant.com or (555) 123-VOICE', yPosition);

  // Save PDF
  doc.save(`receipt_${order.orderNumber}.pdf`);
};
